import React, { useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DialogModal, useHandleOpen } from '@engloba-tech/englobity';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import { NAME_SPACE } from 'i18n';
import { ViewTable, Button, ButtonAdd, READER_REQUEST_TYPES } from 'shared';
import { ROLES, UserContext } from 'auth';
import { PATH } from 'app.routes.const';
import { useReaderRequestsPaginated } from './useReaderRequestsPaginated';
import { useReaderRequestsBuildTable } from './useReaderRequestsBuildTable';
import { useListReaderRequestsStyles } from './listReaderRequests.styles';
import { FormCreateRequest } from './FormCreateRequest';
import { StepperDefectiveTagsRequest } from './StepperDefectiveTagsRequest';
import { ViewErrors } from './ViewErrors';

export function ListReaderRequests() {
  const classes = useListReaderRequestsStyles();
  const $formCreateRequest = useRef(null);
  const $formProcessRequest = useRef(null);
  const {
    readerRequests,
    getReaderRequests,
    errorInfo,
    setErrorInfo,
    createRequest,
    processResponse,
    handleDownload,
    handleExport,
    isCreating
  } = useReaderRequestsPaginated();
  const { t } = useTranslation(NAME_SPACE.READER_REQUESTS);
  const history = useHistory();
  const { rows, headCells } = useReaderRequestsBuildTable(readerRequests, t);
  const { user } = useContext(UserContext);
  const {
    isOpen: isCreateRequestOpen,
    handleClose: handleCloseCreateRequest,
    handleOpen: handleOpenAddCreateRequest
  } = useHandleOpen(false);
  const {
    isOpen: isProcessDefectiveTagsOpen,
    handleClose: handleCloseProcessDefectiveTags,
    handleOpen: handleOpenProcessDefectiveTags
  } = useHandleOpen(true);
  const [selectedRequestId, setSelectedRequestId] = useState();
  const {
    isOpen: isErrorsModalOpen,
    handleClose: handleCloseErrorsModal,
    handleOpen: handleOpenErrorsModal
  } = useHandleOpen(false);
  const [selectedError, setSelectedError] = useState();

  function handleEdit([readerRequestId]) {
    history.push(PATH.READER_REQUESTS.EDIT.replace(':id', readerRequestId));
  }

  function composeActionButtons(rowCount) {
    if (user?.role !== ROLES.PRODUCTION) {
      return [];
    }

    const actionButtons = [
      {
        condition: isAnySelected => !isAnySelected,
        cb: handleOpenAddCreateRequest,
        component: ButtonAdd
      },
      {
        condition: isAnySelected => !isAnySelected && rowCount > 0,
        title: t('actions.export'),
        name: 'export',
        label: 'export',
        cb: handleExport,
        icon: <GetAppIcon />,
        component: Button
      }
    ];
    return actionButtons;
  }

  function handleModalAcceptCreateRequest() {
    $formCreateRequest.current.submit();
  }

  function handleModalCloseCreateRequest() {
    setErrorInfo({ message: '', detail: '' });
    handleCloseCreateRequest();
  }

  async function handleCreateRequestSubmit(request) {
    setErrorInfo({ message: '', detail: '' });
    const created = await createRequest(request);
    if (created) {
      handleCloseCreateRequest();
      if ((created.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) > 0) {
        setSelectedRequestId(created.id);
        handleOpenProcessDefectiveTags();
      }
    }
  }
  function handleModalCloseProcessDefectiveTags() {
    setErrorInfo({ message: '', detail: '' });
    handleCloseProcessDefectiveTags();
  }

  async function handleProcessRequestSubmit(requestId) {
    setErrorInfo({ message: '', detail: '' });
    const result = await processResponse(requestId);
    if (result) {
      handleModalCloseProcessDefectiveTags();
      setSelectedRequestId();
    }
  }

  const handleOpenViewErrors = ([errors]) => {
    setSelectedError(errors);
    handleOpenErrorsModal();
  };

  const handleCloseViewErrors = () => {
    setSelectedError();
    handleCloseErrorsModal();
  };

  return (
    <>
      <div className={classes.request}>
        <ViewTable
          elevation
          spaced
          rows={rows}
          cells={headCells}
          defaultOrderBy={{ property: 'requestDate', direction: 'desc' }}
          mainKey={'requestDate'}
          onFetchData={getReaderRequests}
          totalRows={readerRequests.total}
          serverSidePaging
          emptyText={t('empty')}
          allowRowSelection={false}
          toolbar={{
            title: t('name'),
            actions: composeActionButtons(rows.length)
          }}
          actions={[
            {
              condition: element => !element['errors'],
              whenConditionTrue: null,
              title: t('actions.viewErrors'),
              name: 'view',
              properties: ['errors'],
              label: 'viewErrors',
              cb: handleOpenViewErrors,
              icon: <ReportProblemOutlinedIcon />
            },
            {
              condition: element => !element['responseFileUrl'],
              whenConditionTrue: null,
              title: t('actions.download'),
              name: 'download',
              properties: ['responseFileUrl'],
              label: 'download',
              cb: handleDownload,
              icon: <CloudDownloadOutlinedIcon />
            },
            {
              title: t('actions.view'),
              name: 'edit',
              properties: ['id'],
              label: 'edit this row',
              cb: handleEdit,
              icon: <VisibilityIcon />
            }
          ]}
        />
      </div>
      <DialogModal
        title={t('create.dialogTitle')}
        description=""
        fullWidth
        className={classes.modal}
        isOpen={isCreateRequestOpen}
        onClose={handleModalCloseCreateRequest}
        buttons={[
          {
            children: <CloseIcon />,
            text: t('common:actions.cancel'),
            type: 'secondary',
            onClick: handleModalCloseCreateRequest,
            className: classes.buttonDelete
          },
          {
            children: <DoneIcon />,
            text: t('common:actions.add'),
            type: 'primary',
            onClick: handleModalAcceptCreateRequest,
            className: classes.buttonAdd,
            disabled: isCreating
          }
        ]}
      >
        <FormCreateRequest formRef={$formCreateRequest} onSubmit={handleCreateRequestSubmit} errors={errorInfo} />
      </DialogModal>
      <DialogModal
        title={t('defectiveTagsReplacement.dialogTitle')}
        description=""
        fullWidth
        className={classes.modal}
        isOpen={selectedRequestId && isProcessDefectiveTagsOpen}
      >
        <StepperDefectiveTagsRequest
          formRef={$formProcessRequest}
          errors={errorInfo}
          requestId={selectedRequestId}
          onCancel={handleModalCloseProcessDefectiveTags}
          onSubmit={handleProcessRequestSubmit}
          isBusy={isCreating}
        />
      </DialogModal>
      <DialogModal
        title={t('viewErrors.dialogTitle')}
        description=""
        fullWidth
        className={classes.modal}
        isOpen={selectedError && isErrorsModalOpen}
        onClose={handleCloseViewErrors}
        buttons={[
          {
            children: <CloseIcon />,
            text: t('common:actions.cancel'),
            type: 'secondary',
            onClick: handleCloseViewErrors,
            className: classes.buttonDelete
          }
        ]}
      >
        <ViewErrors requestErrors={selectedError} />
      </DialogModal>
    </>
  );
}
